import { Box, Button, Container, Flex, FormLabel, HStack, Stack, Tag, Text, useToast, VStack } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useAxios } from 'context/AxiosContextProvider';
import { ContentCard } from 'layout/ContentCard';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IoIosWarning } from 'react-icons/io';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import { ChoiceSwitch } from '../../components/ChoiceSwitch';
import { IdentificationResultIcon } from '../../components/Icons';
import Link from '../../components/Link';
import localconfig from '../../config';
import { dateTimeTransformer } from '../../helpers/dateTransformer';
import { displayRow } from '../../helpers/general';
import { dropzone } from '../../helpers/makeFormFields';
import { manageErrorResponse } from '../../helpers/manageErrorResponse';
import useWorkshopQuery from '../../hooks/useWorkshopQuery';
export interface IVerificationForm {
  power_of_attorney: any; //file
  business_licence: any; //file
  identification: any; //file
}

const schema = yup
  .object({
    power_of_attorney: yup.mixed().required(),
    business_licence: yup.mixed().required(),
    identification: yup.mixed().required(),
  })
  .required();

enum WorkshopStatuses {
  RESOLVED = 700,
  REJECTED = 900,
}

const WorkshopVerificationForm = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const axios = useAxios();
  const { id } = useParams();
  const navigate = useNavigate();
  const workshopQuery = useWorkshopQuery(Number(id));
  const workshop = workshopQuery.data;
  const latestWorkshopDUR = workshop?.last_data_update_request;
  const durNotCompleted = latestWorkshopDUR?.status !== WorkshopStatuses.RESOLVED;
  const durRejected = latestWorkshopDUR?.status === WorkshopStatuses.REJECTED;
  const pendingDUR = latestWorkshopDUR ? latestWorkshopDUR?.status < WorkshopStatuses.RESOLVED : false;

  const [notifyCustomer, setNotifyCustomer] = useState(false);
  const {
    handleSubmit,
    formState: { errors, isValid, submitCount },
    control,
    setError,
  } = useForm<IVerificationForm>({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const { mutate, isPending } = useMutation<any, Error, IVerificationForm>({
    mutationFn: async (data) => {
      const formData = new FormData();
      formData.append('workshop_id', id as string);
      formData.append('business_licence', data.business_licence.file as File);
      formData.append('identification', data.identification.file as File);
      formData.append('power_of_attorney', data.power_of_attorney.file as File);
      const response = await axios
        .post(`${localconfig.apiBaseUrl}/op/v1/workshop-verification-request?notify=${notifyCustomer ? 1 : 0}`, formData)
        .catch((err) => {
          manageErrorResponse(toast, err.response);
          return Promise.reject(err);
        });

      return response;
    },
  });

  const onSubmit: SubmitHandler<IVerificationForm> = (data) => {
    mutate(data, {
      onSuccess: () => {
        navigate(`/app/op/workshops/${id}`);
      },
      onError: (error: any) => {
        const { errors } = error.response.data;

        (Object.keys(errors) as Array<keyof IVerificationForm>).forEach((key) => {
          setError(key, {
            type: 'server',
            message: errors[key]!.join('. '),
          });
        });
      },
    });
  };

  if (!id) {
    navigate('/404', { replace: true });
    return null;
  }

  return (
    <>
      <ContentCard
        isLoading={workshopQuery.isFetching}
        maxWidth={{ base: 'full', xl: '75%' }}
        icon={pendingDUR ? <IoIosWarning size={45} color={'orange'} /> : <IdentificationResultIcon boxSize={10} />}
        minH="fit-content"
        header={
          pendingDUR ? (
            <HStack>
              <Box>
                {t('workshop_update_request:header')} {workshop?.firstname} {workshop?.lastname}
              </Box>

              <Box mt={3} color="blue.500" fontSize={20}>
                <Link
                  to={`/app/op/workshop-update-request/${workshop?.pending_data_update_request?.id}`}
                  fontWeight={'400'}
                  fontSize={25}
                  target="_blank"
                >
                  {`(${workshop?.pending_data_update_request?.user_readable_id})`}
                </Link>
              </Box>
            </HStack>
          ) : (
            workshop?.name
          )
        }
        data-test-id="manual-verification-form"
      >
        <Stack w="full">
          <Container as="form" onSubmit={handleSubmit(onSubmit)} opacity={isPending ? 0.5 : 1}>
            <Stack px={{ base: '4', md: '6' }} py={{ base: '5', md: '6' }}>
              <HStack>
                <VStack w={'50%'} alignItems={'flex-start'}>
                  {/*created at*/}
                  {pendingDUR &&
                    displayRow({
                      label: t('forms:created_at.label'),
                      // @ts-ignore
                      content: dateTimeTransformer(latestWorkshopDUR?.created_at ?? '') ?? '-',
                    })}
                  {/*processed at*/}
                  {pendingDUR &&
                    displayRow({
                      label: t('forms:processed_at.label'),
                      // @ts-ignore
                      content: dateTimeTransformer(latestWorkshopDUR?.submitted_at ?? '') ?? '-',
                    })}
                  <VStack>
                    {/*workshop name old and new values*/}
                    {displayRow({
                      label: t('workshop:name.label'),
                      content:
                        latestWorkshopDUR?.new_values?.name && !durRejected ? (
                          <Box color="blue.500" fontWeight="semibold">
                            {latestWorkshopDUR?.new_values?.name ?? '-'}
                          </Box>
                        ) : (
                          (workshop?.name ?? '-')
                        ),
                    })}

                    {latestWorkshopDUR?.new_values?.name && durNotCompleted && !durRejected && (
                      <HStack alignItems={'flex-start'}>
                        <Text fontSize="sm" whiteSpace={'nowrap'} fontWeight="semibold" color="transparent">
                          {t('workshop:name.label')}
                        </Text>
                        <Text fontSize="sm" whiteSpace={'nowrap'} as="s">
                          {latestWorkshopDUR?.old_values?.name}
                        </Text>
                      </HStack>
                    )}
                  </VStack>
                  <Box display="flex" flexDir="column">
                    {/*vat number old and new values*/}
                    {displayRow({
                      label: t('workshop:vat_number.label'),
                      content:
                        latestWorkshopDUR?.new_values?.vat_number && !durRejected ? (
                          <Box color="blue.500" fontWeight="semibold">
                            {latestWorkshopDUR?.new_values?.vat_number ?? '-'}
                          </Box>
                        ) : (
                          (workshop?.vat_number ?? '-')
                        ),
                    })}
                    {latestWorkshopDUR?.new_values?.vat_number && durNotCompleted && !durRejected && (
                      <HStack alignItems={'flex-start'}>
                        <Text fontSize="sm" whiteSpace={'nowrap'} fontWeight="semibold" color="transparent">
                          {t('workshop:vat_number.label')}
                        </Text>
                        <Text fontSize="sm" whiteSpace={'nowrap'} as="s">
                          {latestWorkshopDUR?.old_values?.vat_number}
                        </Text>

                        {(workshop?.vat_validation_information?.name || workshop?.vat_validation_information?.address) && (
                          <Tag size={'sm'} variant="solid" bgColor={workshop?.vat_validation_information?.valid ? 'green' : 'orange.200'}>
                            <Text as={'div'}>{workshop?.vat_validation_information?.name}</Text>,
                            <Text as={'div'}>{workshop?.vat_validation_information?.address}</Text>
                          </Tag>
                        )}
                      </HStack>
                    )}
                  </Box>
                  <VStack>
                    {/*address old and new values*/}
                    {displayRow({
                      label: t('workshop:address.label'),
                      content:
                        latestWorkshopDUR?.new_values?.address && !durRejected ? (
                          <Box color="blue.500" fontWeight="semibold">
                            {latestWorkshopDUR?.new_values?.address ?? '-'}
                          </Box>
                        ) : (
                          (workshop?.address ?? '-')
                        ),
                    })}
                    {latestWorkshopDUR?.new_values?.address && durNotCompleted && !durRejected && (
                      <HStack alignItems={'flex-start'}>
                        <Text fontSize="sm" whiteSpace={'nowrap'} fontWeight="semibold" color="transparent">
                          {t('workshop:address.label')}
                        </Text>
                        <Text fontSize="sm" whiteSpace={'nowrap'} as="s">
                          {latestWorkshopDUR?.old_values?.address}
                        </Text>
                      </HStack>
                    )}
                  </VStack>

                  <VStack>
                    {/*city old and new values*/}
                    {displayRow({
                      label: t('workshop:city.label'),
                      content:
                        latestWorkshopDUR?.new_values?.city && !durRejected ? (
                          <Box color="blue.500" fontWeight="semibold">
                            {latestWorkshopDUR?.new_values?.city ?? '-'}
                          </Box>
                        ) : (
                          (workshop?.city ?? '-')
                        ),
                    })}
                    {latestWorkshopDUR?.new_values?.city && durNotCompleted && !durRejected && (
                      <HStack alignItems={'flex-start'}>
                        <Text fontSize="sm" whiteSpace={'nowrap'} fontWeight="semibold" color="transparent">
                          {t('workshop:city.label')}
                        </Text>
                        <Text fontSize="sm" whiteSpace={'nowrap'} as="s">
                          {latestWorkshopDUR?.old_values?.city}
                        </Text>
                      </HStack>
                    )}
                  </VStack>
                  <VStack>
                    {/*zipcode old and new values*/}
                    {displayRow({
                      label: t('workshop:zipcode.label'),
                      content:
                        latestWorkshopDUR?.new_values?.zipcode && !durRejected ? (
                          <Box color="blue.500" fontWeight="semibold">
                            {latestWorkshopDUR?.new_values?.zipcode ?? '-'}
                          </Box>
                        ) : (
                          (workshop?.zipcode ?? '-')
                        ),
                    })}
                    {latestWorkshopDUR?.new_values?.zipcode && durNotCompleted && !durRejected && (
                      <HStack alignItems={'flex-start'}>
                        <Text fontSize="sm" whiteSpace={'nowrap'} fontWeight="semibold" color="transparent">
                          {t('workshop:zipcode.label')}
                        </Text>
                        <Text fontSize="sm" whiteSpace={'nowrap'} as="s">
                          {latestWorkshopDUR?.old_values?.zipcode}
                        </Text>
                      </HStack>
                    )}
                  </VStack>
                </VStack>
                <VStack width={'50%'} alignItems={'flex-start'}>
                  <VStack>
                    {/*language old and new values*/}
                    {displayRow({
                      label: t('workshop:language.label'),
                      content:
                        latestWorkshopDUR?.new_values?.language && !durRejected ? (
                          <Box color="blue.500" fontWeight="semibold">
                            {t(`location:languages.${latestWorkshopDUR?.new_values?.language.toUpperCase()}`)}
                          </Box>
                        ) : workshop?.language ? (
                          t(`location:languages.${workshop?.language.toUpperCase()}`)
                        ) : (
                          '-'
                        ),
                    })}
                    {latestWorkshopDUR?.new_values?.language && durNotCompleted && !durRejected && (
                      <HStack alignItems={'flex-start'}>
                        <Text fontSize="sm" whiteSpace={'nowrap'} fontWeight="semibold" color="transparent">
                          {t('workshop:language.label')}
                        </Text>
                        <Text fontSize="sm" whiteSpace={'nowrap'} as="s">
                          {latestWorkshopDUR?.old_values?.language}
                        </Text>
                      </HStack>
                    )}
                  </VStack>
                  <VStack>
                    {/*country old and new values*/}
                    {displayRow({
                      label: t('workshop:country.label'),
                      content:
                        latestWorkshopDUR?.new_values?.country && !durRejected ? (
                          <Box color="blue.500" fontWeight="semibold">
                            {t(`location:countries.${latestWorkshopDUR?.new_values?.country.toUpperCase()}`)}
                          </Box>
                        ) : workshop?.country ? (
                          t(`location:countries.${workshop?.country.toUpperCase()}`)
                        ) : (
                          '-'
                        ),
                    })}
                    {latestWorkshopDUR?.new_values?.country && durNotCompleted && !durRejected && (
                      <HStack alignItems={'flex-start'}>
                        <Text fontSize="sm" whiteSpace={'nowrap'} fontWeight="semibold" color="transparent">
                          {t('workshop:country.label')}
                        </Text>
                        <Text fontSize="sm" whiteSpace={'nowrap'} as="s">
                          {latestWorkshopDUR?.old_values?.country}
                        </Text>
                      </HStack>
                    )}
                  </VStack>
                  <VStack>
                    {/*first name old and new values*/}
                    {displayRow({
                      label: t('workshop:firstname.label'),
                      content:
                        latestWorkshopDUR?.new_values?.firstname && !durRejected ? (
                          <Box color="blue.500" fontWeight="semibold">
                            {latestWorkshopDUR?.new_values?.firstname ?? '-'}
                          </Box>
                        ) : (
                          (workshop?.firstname ?? '-')
                        ),
                    })}
                    {latestWorkshopDUR?.new_values?.firstname && durNotCompleted && !durRejected && (
                      <HStack alignItems={'flex-start'}>
                        <Text fontSize="sm" whiteSpace={'nowrap'} fontWeight="semibold" color="transparent">
                          {t('workshop:firstname.label')}
                        </Text>
                        <Text fontSize="sm" whiteSpace={'nowrap'} as="s">
                          {latestWorkshopDUR?.old_values?.firstname}
                        </Text>
                      </HStack>
                    )}
                  </VStack>
                  <VStack>
                    {/*last name old and new values*/}
                    {displayRow({
                      label: t('workshop:lastname.label'),
                      content:
                        latestWorkshopDUR?.new_values?.lastname && !durRejected ? (
                          <Box color="blue.500" fontWeight="semibold">
                            {latestWorkshopDUR?.new_values?.lastname ?? '-'}
                          </Box>
                        ) : (
                          (workshop?.lastname ?? '-')
                        ),
                    })}
                    {latestWorkshopDUR?.new_values?.lastname && durNotCompleted && !durRejected && (
                      <HStack alignItems={'flex-start'}>
                        <Text fontSize="sm" whiteSpace={'nowrap'} fontWeight="semibold" color="transparent">
                          {t('workshop:lastname.label')}
                        </Text>
                        <Text fontSize="sm" whiteSpace={'nowrap'} as="s">
                          {latestWorkshopDUR?.old_values?.lastname}
                        </Text>
                      </HStack>
                    )}
                  </VStack>
                  <VStack>
                    {/*email old and new values*/}
                    {displayRow({
                      label: t('workshop:email.label'),
                      content:
                        latestWorkshopDUR?.new_values?.email && !durRejected ? (
                          <Box color="blue.500" fontWeight="semibold">
                            {latestWorkshopDUR?.new_values?.email ?? '-'}
                          </Box>
                        ) : (
                          (workshop?.email ?? '-')
                        ),
                    })}
                    {latestWorkshopDUR?.new_values?.email && durNotCompleted && !durRejected && (
                      <HStack alignItems={'flex-start'}>
                        <Text fontSize="sm" whiteSpace={'nowrap'} fontWeight="semibold" color="transparent">
                          {t('workshop:email.label')}
                        </Text>
                        <Text fontSize="sm" whiteSpace={'nowrap'} as="s">
                          {latestWorkshopDUR?.old_values?.email}
                        </Text>
                      </HStack>
                    )}
                  </VStack>
                  <VStack>
                    {/*telephone old and new values*/}
                    {displayRow({
                      label: t('workshop:telephone.label'),
                      content:
                        latestWorkshopDUR?.new_values?.telephone && !durRejected ? (
                          <Box color="blue.500" fontWeight="semibold">
                            {latestWorkshopDUR?.new_values?.telephone ?? '-'}
                          </Box>
                        ) : (
                          (workshop?.telephone ?? '-')
                        ),
                    })}
                    {latestWorkshopDUR?.new_values?.telephone && durNotCompleted && !durRejected && (
                      <HStack alignItems={'flex-start'}>
                        <Text fontSize="sm" whiteSpace={'nowrap'} fontWeight="semibold" color="transparent">
                          {t('workshop:telephone.label')}
                        </Text>
                        <Text fontSize="sm" whiteSpace={'nowrap'} as="s" overflowWrap={'break-word'} wordBreak={'break-word'}>
                          {latestWorkshopDUR?.old_values?.telephone}
                        </Text>
                      </HStack>
                    )}
                  </VStack>
                </VStack>
              </HStack>
            </Stack>
          </Container>
        </Stack>
      </ContentCard>

      <ContentCard header={t('workshop_verification_request:create.header')} maxWidth={{ base: 'full', xl: '75%' }}>
        <Stack w="full">
          <Container as="form" onSubmit={handleSubmit(onSubmit)} opacity={isPending ? 0.5 : 1}>
            <Stack px={{ base: '4', md: '6' }} py={{ base: '5', md: '6' }}>
              <FormLabel>{t('workshop_verification_request:notify_verification.label')}</FormLabel>
              <ChoiceSwitch
                width={258}
                choice1={{
                  label: t('common:no'),
                  value: 'No',
                }}
                choice2={{
                  label: t('common:yes'),
                  value: 'Yes',
                }}
                bgColor={'gray.200'}
                border={'1'}
                defaultValue={'No'}
                disabled={false}
                onChange={() => setNotifyCustomer((prevState) => !prevState)}
              />
              <Stack direction={['column']} pt={10}>
                {dropzone({
                  name: 'business_licence',
                  label: t('workshop_verification_request:business_licence.label'),
                  control,
                  errors,
                })}

                {dropzone({
                  name: 'identification',
                  label: t('workshop_verification_request:identification.label'),
                  control,
                  errors,
                })}

                {dropzone({
                  name: 'power_of_attorney',
                  label: t('workshop_verification_request:power_of_attorney.label'),
                  control,
                  errors,
                })}
              </Stack>

              <Flex direction="row-reverse" py="4" px={{ base: '4', md: '6' }}>
                <Button type="submit" variant="primary" ml={2} disabled={(submitCount! > 0 && !isValid) || isPending}>
                  {t('common:create')}
                </Button>
              </Flex>
            </Stack>
          </Container>
        </Stack>
      </ContentCard>
    </>
  );
};

export default WorkshopVerificationForm;
